<script setup lang="ts">
import { computed, ref, toRef } from 'vue';

import type { SensitiveCredentialsApi } from '~/services/sensitive-credentials-api/sensitive-credentials-api.types';

import {
  formatCredentialLoginForDisplay,
  formatManufacturePlatformLabel,
} from '~/utils/formatters';

import usePartnerCredentials from '../queries/usePartnerCredentials';
import { buildCredentialPlatformGroups } from '../utils/buildCredentialPlatformGroups';
import ResetCredentialModal from './ResetCredentialModal.vue';

const props = defineProps<{
  partnerId: number;
}>();

const partnerIdRef = toRef(() => props.partnerId);

const {
  data: credentialsResponse,
  error: credentialsError,
  isLoading,
} = usePartnerCredentials(partnerIdRef);

const credentialItems = computed(
  () => credentialsResponse.value?.data?.items ?? [],
);

const credentialsTotal = computed(
  () => credentialsResponse.value?.data?.total ?? 0,
);

const hasTruncatedList = computed(
  () => credentialsTotal.value > credentialItems.value.length,
);

const platformGroups = computed(() => buildCredentialPlatformGroups(credentialItems.value));

const showResetModal = ref(false);
const selectedCredential = ref<null | SensitiveCredentialsApi.Credential>(null);

function closeResetModal() {
  showResetModal.value = false;
  selectedCredential.value = null;
}

function displayLogin(credential: SensitiveCredentialsApi.Credential): string {
  return formatCredentialLoginForDisplay(credential.login);
}

function openResetModal(credential: SensitiveCredentialsApi.Credential) {
  selectedCredential.value = credential;
  showResetModal.value = true;
}

function platformLabel(platform: string): string {
  return formatManufacturePlatformLabel(platform);
}

function resetButtonLabel(credential: SensitiveCredentialsApi.Credential): string {
  const login = displayLogin(credential);
  return `Resetar credencial (${login})`;
}
</script>

<template>
  <div v-permission="['tech_n1_support:edit']" class="col-12 mb-3">
    <div class="card">
      <div class="card-header">
        <h3 class="card-title">Credenciais do Parceiro</h3>
      </div>
      <div class="card-body">
        <div v-if="isLoading">
          <div class="placeholder-glow">
            <div class="placeholder col-8 mb-2" />
            <div class="placeholder col-12" />
            <div class="placeholder col-6" />
          </div>
        </div>

        <div v-else-if="credentialsError" class="alert alert-danger" role="alert">
          Erro ao carregar credenciais do parceiro.
        </div>

        <div
          v-else-if="credentialItems.length === 0"
          class="text-secondary"
        >
          Nenhuma credencial cadastrada.
        </div>

        <div v-else>
          <div
            v-if="hasTruncatedList"
            class="alert alert-warning py-2 mb-3 small"
            role="status"
          >
            Exibindo {{ credentialItems.length }} de {{ credentialsTotal }} credenciais.
            Alguns logins podem não aparecer nesta lista.
          </div>

          <div class="table-responsive partner-credentials-table-scroll">
            <table class="table table-sm table-vcenter table-striped table-hover mb-0">
              <thead>
                <tr>
                  <th scope="col">Plataforma</th>
                  <th scope="col">Login</th>
                  <th scope="col" class="text-end partner-credentials-actions-col">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                <template
                  v-for="group in platformGroups"
                  :key="group.platform"
                >
                  <template v-if="group.kind === 'single'">
                    <tr>
                      <td class="fw-medium">
                        {{ platformLabel(group.platform) }}
                      </td>
                      <td
                        class="partner-credentials-login-cell text-truncate"
                        :title="group.credential.login ?? undefined"
                      >
                        {{ displayLogin(group.credential) }}
                      </td>
                      <td class="text-end partner-credentials-actions-col">
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-danger"
                          :aria-label="resetButtonLabel(group.credential)"
                          @click="openResetModal(group.credential)"
                        >
                          Resetar
                        </button>
                      </td>
                    </tr>
                  </template>

                  <template v-else>
                    <tr class="partner-credentials-group-row">
                      <td
                        colspan="3"
                        class="py-2"
                      >
                        <span class="fw-semibold">
                          {{ platformLabel(group.platform) }}
                        </span>
                        <span class="badge bg-secondary-lt text-secondary ms-2">
                          {{ group.credentials.length }} credenciais
                        </span>
                      </td>
                    </tr>
                    <tr
                      v-for="credential in group.credentials"
                      :key="credential.credential_id"
                      class="partner-credentials-nested-row"
                    >
                      <td class="partner-credentials-nested-platform" aria-hidden="true" />
                      <td
                        class="partner-credentials-login-cell text-truncate"
                        :title="credential.login ?? undefined"
                      >
                        {{ displayLogin(credential) }}
                      </td>
                      <td class="text-end partner-credentials-actions-col">
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-danger"
                          :aria-label="resetButtonLabel(credential)"
                          @click="openResetModal(credential)"
                        >
                          Resetar
                        </button>
                      </td>
                    </tr>
                  </template>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <ResetCredentialModal
      v-if="selectedCredential"
      v-model="showResetModal"
      :partner-id="partnerId"
      :credential="selectedCredential"
      @close="closeResetModal"
    />
  </div>
</template>

<style scoped lang="scss">
.partner-credentials-table-scroll {
  max-height: min(70vh, 32rem);
  overflow: auto;
}

.partner-credentials-table-scroll thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--tblr-bg-surface, #fff);
  box-shadow: 0 1px 0 var(--tblr-border-color, rgba(0, 0, 0, 0.08));
}

.partner-credentials-login-cell {
  max-width: 18rem;
}

.partner-credentials-actions-col {
  width: 7.5rem;
  white-space: nowrap;
}

.partner-credentials-group-row td {
  background: var(--tblr-bg-surface-secondary, rgba(0, 0, 0, 0.02));
  border-top-width: 2px;
}

.partner-credentials-nested-platform {
  border-left: 3px solid var(--tblr-primary, #206bc4);
  width: 0.5rem;
  padding: 0;
}
</style>
